import React, { useState } from 'react';
import {
    Box,
    Button,
    Card,
    CardActionArea,
    CardContent,
    Checkbox,
    Container,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    Skeleton,
    Stack,
    Typography,
    useMediaQuery,
} from '@mui/material';
import { DataGrid, GridColDef, GridRenderCellParams, GridRowParams } from '@mui/x-data-grid';
import { useTheme } from '@mui/material/styles';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import { IHutBookEntry } from '../Interfaces/IHutBook';
import { useDeleteHutBookEntry, useGetHutBookEntries } from '../hooks/hutBook';
import { useAlert } from '../core/AlertContext';
import { BrandedDataGridOverlay } from '../core/BrandedDataGridOverlay';
import RowActionsMenu from '../core/RowActionsMenu';
import CompactUserDisplay from '../core/CompactUserDisplay';
import HutBookEntryEdit from './HutBookEntryEdit';
import AuditInfoDialog from '../core/AuditInfoDialog';
import { checkPermission } from '../utils/permission';
import DetailScreenHeader from '../core/DetailScreenHeader';
import { EVENT_DIALOG_MAX_WIDTH, EVENT_DIALOG_PAPER_SX } from '../calendar/dialogLayout';
import PageHeader from '../core/PageHeader';
import PvtaCard from '../core/PvtaCard';
import StatusChip from '../core/StatusChip';
import { primaryButtonSx, secondaryButtonSx, dangerButtonSx, makeIconCircleSx } from '../core/pvtaStyles';
import MeetingRoomOutlinedIcon from '@mui/icons-material/MeetingRoomOutlined';
import DateRangeOutlinedIcon from '@mui/icons-material/DateRangeOutlined';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined';

const formatDate = (value?: string | null): string => {
    if (!value) {
        return '-';
    }
    return new Intl.DateTimeFormat('de-DE', { dateStyle: 'medium' }).format(new Date(value));
};

const formatDateTime = (value?: string | null): string => {
    if (!value) {
        return '-';
    }
    return new Intl.DateTimeFormat('de-DE', {
        dateStyle: 'short',
        timeStyle: 'short',
    }).format(new Date(value));
};

const getDisplayName = (user?: { displayName: string } | null): string => user?.displayName ?? '-';

// Anzahl Nächte zwischen zwei Datumswerten (für die Detailansicht).
const nightsBetween = (from?: string | null, to?: string | null): number | null => {
    if (!from || !to) {
        return null;
    }
    const start = new Date(from);
    const end = new Date(to);
    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
        return null;
    }
    const diff = Math.round((end.getTime() - start.getTime()) / 86400000);
    return diff > 0 ? diff : null;
};

// Farben je Zimmer (zyklisch)

const HutBookOverview: React.FC = () => {
    const isAdmin = checkPermission(2);
    const [includeDeleted, setIncludeDeleted] = useState(false);
    const [createDialogOpen, setCreateDialogOpen] = useState(false);
    const [editCandidate, setEditCandidate] = useState<IHutBookEntry | null>(null);
    const [detailsCandidate, setDetailsCandidate] = useState<IHutBookEntry | null>(null);
    const [deleteCandidate, setDeleteCandidate] = useState<IHutBookEntry | null>(null);
    const [infoCandidate, setInfoCandidate] = useState<IHutBookEntry | null>(null);
    const { hutBookEntryArray, isLoadingHutBookEntryArray } = useGetHutBookEntries({
        includeDeleted: isAdmin && includeDeleted,
    });
    const { deleteHutBookEntry, isDeletingHutBookEntry } = useDeleteHutBookEntry();
    const { showAlert } = useAlert();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const handleDelete = async () => {
        if (!deleteCandidate) {
            return;
        }
        try {
            await deleteHutBookEntry(deleteCandidate.id);
            showAlert({ text: 'Hüttenbuch-Eintrag erfolgreich gelöscht.' });
            setDeleteCandidate(null);
        } catch {
            showAlert({ text: 'Fehler beim Löschen des Hüttenbuch-Eintrags.', severity: 'error' });
        }
    };

    const columns: GridColDef<IHutBookEntry>[] = [
        {
            field: 'entry_for',
            headerName: 'Für wen',
            minWidth: 210,
            flex: 1.1,
            renderCell: (params: GridRenderCellParams<IHutBookEntry>) => (
                <Box sx={{ display: 'flex', alignItems: 'center', height: '100%', width: '100%' }}>
                    <CompactUserDisplay user={params.row.entry_for} />
                </Box>
            ),
        },
        {
            field: 'room',
            headerName: 'Zimmer',
            minWidth: 170,
            flex: 0.9,
            valueGetter: (_value, row) => row.room.name,
        },
        {
            field: 'date_from',
            headerName: 'Von',
            minWidth: 120,
            flex: 0.6,
            valueGetter: (_value, row) => formatDate(row.date_from),
        },
        {
            field: 'date_to',
            headerName: 'Bis',
            minWidth: 120,
            flex: 0.6,
            valueGetter: (_value, row) => formatDate(row.date_to),
        },
        {
            field: 'created_by',
            headerName: 'Erstellt von',
            minWidth: 210,
            flex: 1,
            renderCell: (params: GridRenderCellParams<IHutBookEntry>) => (
                <Box sx={{ display: 'flex', alignItems: 'center', height: '100%', width: '100%' }}>
                    <CompactUserDisplay user={params.row.created_by} />
                </Box>
            ),
        },
        {
            field: 'created_at',
            headerName: 'Erstellt am',
            minWidth: 160,
            flex: 0.8,
            valueGetter: (_value, row) => formatDateTime(row.created_at),
        },
        {
            field: 'actions',
            headerName: 'Aktionen',
            minWidth: 90,
            sortable: false,
            filterable: false,
            align: 'center',
            headerAlign: 'center',
            renderCell: (params: GridRenderCellParams<IHutBookEntry>) => (
                <RowActionsMenu
                    items={[
                        {
                            label: 'Anzeigen',
                            icon: <VisibilityOutlinedIcon fontSize="small" />,
                            onClick: () => setDetailsCandidate(params.row),
                        },
                        ...(isAdmin ? [
                            {
                                label: 'Bearbeiten',
                                icon: <EditOutlinedIcon fontSize="small" />,
                                onClick: () => setEditCandidate(params.row),
                            },
                            {
                                label: 'Löschen',
                                icon: <DeleteOutlineIcon fontSize="small" />,
                                disabled: Boolean(params.row.deleted_at),
                                onClick: () => setDeleteCandidate(params.row),
                            },
                            {
                                label: 'Weitere Informationen',
                                icon: <InfoOutlinedIcon fontSize="small" />,
                                onClick: () => setInfoCandidate(params.row),
                            },
                        ] : []),
                    ]}
                />
            ),
        },
    ];

    if (isLoadingHutBookEntryArray) {
        return (
            <Box sx={{ py: { xs: 3, md: 4 } }}>
                <Container maxWidth="xl" sx={{ px: { xs: 0, sm: 2 } }}>
                    <Skeleton variant="text" width={200} height={44} sx={{ mb: 3 }} />
                    <Stack spacing={1}>
                        <Skeleton variant="rounded" height={48} />
                        {[0, 1, 2, 3].map((item) => (
                            <Skeleton key={item} variant="rounded" height={52} />
                        ))}
                    </Stack>
                </Container>
            </Box>
        );
    }

    return (
        <Box sx={{ py: { xs: 3, md: 4 } }}>
            <Container maxWidth="xl" sx={{ px: { xs: 0, sm: 2 } }}>
                <PageHeader title="Hüttenbuch" subtitle="Übernachtungen im Hüttenbuch dokumentieren." />
                <PvtaCard large sx={{ p: { xs: 2, sm: 2.5 }, width: '100%', maxWidth: '100%', minWidth: 0, boxSizing: 'border-box' }}>
                <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    justifyContent="space-between"
                    alignItems={{ xs: 'stretch', sm: 'center' }}
                    spacing={1.5}
                    sx={{ mb: 2, minWidth: 0 }}
                >
                    {isAdmin ? (
                        <FormControlLabel
                            control={(
                                <Checkbox
                                    checked={includeDeleted}
                                    onChange={(event) => setIncludeDeleted(event.target.checked)}
                                />
                            )}
                            label="Gelöschte anzeigen"
                        />
                    ) : <span />}
                    <Button
                        startIcon={<AddIcon />}
                        onClick={() => setCreateDialogOpen(true)}
                        sx={{ ...primaryButtonSx, width: { xs: '100%', sm: 'auto' } }}
                    >
                        Eintrag anlegen
                    </Button>
                </Stack>
                {!isMobile ? (
                    <Box sx={{ width: '100%' }}>
                        <DataGrid
                            rows={hutBookEntryArray}
                            columns={columns}
                            disableRowSelectionOnClick
                            onRowClick={(params: GridRowParams<IHutBookEntry>) => setDetailsCandidate(params.row)}
                            autoHeight
                            pageSizeOptions={[10, 25, 50]}
                            slots={{
                                noRowsOverlay: BrandedDataGridOverlay,
                                noResultsOverlay: BrandedDataGridOverlay,
                            }}
                            sx={{
                                border: 0,
                                '& .MuiDataGrid-cell': {
                                    display: 'flex',
                                    alignItems: 'center',
                                },
                                '& .MuiDataGrid-row': {
                                    cursor: 'pointer',
                                },
                            }}
                        />
                    </Box>
                ) : (
                    <Stack spacing={1.5}>
                        {hutBookEntryArray.map((entry) => (
                            <Card key={entry.id} variant="outlined">
                                <CardActionArea onClick={() => setDetailsCandidate(entry)}>
                                    <CardContent>
                                        <Stack spacing={1}>
                                            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                                                {getDisplayName(entry.entry_for)}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                Zimmer: {entry.room.name}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                {formatDate(entry.date_from)} bis {formatDate(entry.date_to)}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                Erstellt von {getDisplayName(entry.created_by)} am {formatDateTime(entry.created_at)}
                                            </Typography>
                                        </Stack>
                                    </CardContent>
                                </CardActionArea>
                            </Card>
                        ))}
                    </Stack>
                )}
                </PvtaCard>
                <Dialog
                    open={createDialogOpen}
                    onClose={() => setCreateDialogOpen(false)}
                    maxWidth={EVENT_DIALOG_MAX_WIDTH}
                    fullWidth
                    fullScreen={isMobile}
                    PaperProps={EVENT_DIALOG_PAPER_SX}
                >
                    <DetailScreenHeader
                        title="Hüttenbuch-Eintrag anlegen"
                        onClose={() => setCreateDialogOpen(false)}
                    />
                    <DialogContent sx={{ pt: 2 }}>
                        {createDialogOpen ? <HutBookEntryEdit onClose={() => setCreateDialogOpen(false)} showHeading={false} /> : null}
                    </DialogContent>
                </Dialog>
                <Dialog
                    open={editCandidate !== null}
                    onClose={() => setEditCandidate(null)}
                    maxWidth={EVENT_DIALOG_MAX_WIDTH}
                    fullWidth
                    fullScreen={isMobile}
                    PaperProps={EVENT_DIALOG_PAPER_SX}
                >
                    <DetailScreenHeader
                        title="Hüttenbuch-Eintrag bearbeiten"
                        onClose={() => setEditCandidate(null)}
                    />
                    <DialogContent sx={{ pt: 2 }}>
                        {editCandidate ? <HutBookEntryEdit initialEntry={editCandidate} onClose={() => setEditCandidate(null)} showHeading={false} /> : null}
                    </DialogContent>
                </Dialog>
                <Dialog
                    open={detailsCandidate !== null}
                    onClose={() => setDetailsCandidate(null)}
                    maxWidth={EVENT_DIALOG_MAX_WIDTH}
                    fullWidth
                    fullScreen={isMobile}
                    PaperProps={EVENT_DIALOG_PAPER_SX}
                >
                    <DetailScreenHeader
                        title="Hüttenbuch-Eintrag"
                        onClose={() => setDetailsCandidate(null)}
                    />
                    <DialogContent sx={{ pt: 2 }}>
                        <Stack spacing={2} sx={{ minWidth: 0 }}>
                            <PvtaCard sx={{ p: 2.5, minWidth: 0 }}>
                                <Typography variant="caption" sx={{ color: 'var(--pvta-muted)', fontWeight: 600 }}>
                                    Für wen
                                </Typography>
                                <Box sx={{ mt: 1 }}>
                                    <CompactUserDisplay user={detailsCandidate?.entry_for} size={44} />
                                </Box>
                                <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mt: 2 }}>
                                    <Box sx={makeIconCircleSx(36)}>
                                        <MeetingRoomOutlinedIcon fontSize="small" />
                                    </Box>
                                    <Box sx={{ minWidth: 0 }}>
                                        <Typography variant="caption" sx={{ color: 'var(--pvta-muted)' }}>Zimmer</Typography>
                                        <Typography sx={{ fontWeight: 700, color: 'var(--pvta-heading)' }}>
                                            {detailsCandidate?.room.name ?? '-'}
                                        </Typography>
                                    </Box>
                                </Stack>
                            </PvtaCard>

                            <PvtaCard sx={{ p: 2.5, minWidth: 0 }}>
                                <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
                                    <Box sx={makeIconCircleSx(36)}>
                                        <DateRangeOutlinedIcon fontSize="small" />
                                    </Box>
                                    <Typography sx={{ fontWeight: 700, color: 'var(--pvta-heading)', flex: 1, minWidth: 0 }}>
                                        Zeitraum
                                    </Typography>
                                    {(() => {
                                        const nights = nightsBetween(detailsCandidate?.date_from, detailsCandidate?.date_to);
                                        return nights ? (
                                            <StatusChip tone="green" label={`${nights} ${nights === 1 ? 'Nacht' : 'Nächte'}`} />
                                        ) : null;
                                    })()}
                                </Stack>
                                <Stack direction="row" spacing={2}>
                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                        <Typography variant="caption" sx={{ color: 'var(--pvta-muted)' }}>Von</Typography>
                                        <Typography sx={{ fontWeight: 600 }}>{formatDate(detailsCandidate?.date_from)}</Typography>
                                    </Box>
                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                        <Typography variant="caption" sx={{ color: 'var(--pvta-muted)' }}>Bis</Typography>
                                        <Typography sx={{ fontWeight: 600 }}>{formatDate(detailsCandidate?.date_to)}</Typography>
                                    </Box>
                                </Stack>
                            </PvtaCard>

                            <PvtaCard sx={{ p: 2.5, minWidth: 0 }}>
                                <Stack direction="row" spacing={1.5} alignItems="flex-start">
                                    <Box sx={makeIconCircleSx(36)}>
                                        <PersonOutlineIcon fontSize="small" />
                                    </Box>
                                    <Box sx={{ minWidth: 0, flex: 1 }}>
                                        <Typography variant="caption" sx={{ color: 'var(--pvta-muted)' }}>Erstellt von</Typography>
                                        <Box sx={{ mt: 0.5 }}>
                                            <CompactUserDisplay user={detailsCandidate?.created_by} size={32} />
                                        </Box>
                                    </Box>
                                </Stack>
                                <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mt: 2 }}>
                                    <Box sx={makeIconCircleSx(36)}>
                                        <AccessTimeOutlinedIcon fontSize="small" />
                                    </Box>
                                    <Box sx={{ minWidth: 0 }}>
                                        <Typography variant="caption" sx={{ color: 'var(--pvta-muted)' }}>Erstellt am</Typography>
                                        <Typography sx={{ color: 'var(--pvta-text)' }}>{formatDateTime(detailsCandidate?.created_at)}</Typography>
                                    </Box>
                                </Stack>
                            </PvtaCard>
                        </Stack>
                    </DialogContent>
                </Dialog>
                <Dialog open={deleteCandidate !== null} onClose={() => setDeleteCandidate(null)}>
                    <DialogTitle>Hüttenbuch-Eintrag löschen?</DialogTitle>
                    <DialogContent>
                        <Typography variant="body2">
                            Soll der Hüttenbuch-Eintrag für &quot;{getDisplayName(deleteCandidate?.entry_for)}&quot; wirklich gelöscht werden?
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            Hinweis: Der Eintrag wird archiviert und kann für Admins weiter eingeblendet werden.
                        </Typography>
                    </DialogContent>
                    <DialogActions sx={{ px: 3, pb: 2 }}>
                        <Button onClick={() => setDeleteCandidate(null)} sx={secondaryButtonSx}>Abbrechen</Button>
                        <Button sx={dangerButtonSx} disabled={isDeletingHutBookEntry} onClick={() => { void handleDelete(); }}>
                            Löschen
                        </Button>
                    </DialogActions>
                </Dialog>
                <AuditInfoDialog
                    open={infoCandidate !== null}
                    onClose={() => setInfoCandidate(null)}
                    title="Audit-Informationen"
                    createdBy={infoCandidate?.created_by}
                    createdAt={infoCandidate?.created_at}
                    updatedBy={infoCandidate?.updated_by}
                    updatedAt={infoCandidate?.updated_at}
                    deletedBy={infoCandidate?.deleted_by}
                    deletedAt={infoCandidate?.deleted_at}
                />
            </Container>
        </Box>
    );
};

export default HutBookOverview;
